/**
 * Form Translation Service
 * Handles translation of form fields to Spanish using the existing translation infrastructure
 */

import { translateMessageText } from './messageTranslationService';
import { Form, FormField } from '@/firebase/formService';

/**
 * Translate all form fields to Spanish
 * Only translates fields that don't already have Spanish translations
 */
export async function translateFormFields(formData: Partial<Form>): Promise<{
  success: boolean;
  data?: Partial<Form>;
  error?: string;
}> {
  try {
    const translated: Partial<Form> = { ...formData };
    let translationCount = 0;
    let skippedCount = 0;
    
    // 1. Translate form name
    // Translate if no Spanish name exists, OR if Spanish name is same as English (incorrect translation)
    if (formData.name && (!formData.name_es || formData.name_es === formData.name)) {
      console.log(`[translateFormFields] Translating form name: "${formData.name}"`);
      const translatedName = await translateMessageText(formData.name, 'es', 'en');
      console.log(`[translateFormFields] Translation result: "${translatedName}"`);
      // Only set if translation is different from original (to avoid saving English as Spanish)
      if (translatedName && translatedName !== formData.name && translatedName.trim().length > 0) {
        translated.name_es = translatedName;
        translationCount++;
        console.log(`[translateFormFields] ✓ Form name translated successfully`);
      } else {
        console.warn(`[translateFormFields] ✗ Skipping form name translation - same as original: "${formData.name}" -> "${translatedName}"`);
        skippedCount++;
      }
    } else if (formData.name && formData.name_es && formData.name_es !== formData.name) {
      console.log(`[translateFormFields] Form name already has valid Spanish translation: "${formData.name_es}"`);
    }
    
    // 1.5. Translate form description
    // Translate if no Spanish description exists, OR if Spanish description is same as English (incorrect translation)
    if (formData.description && (!formData.description_es || formData.description_es === formData.description)) {
      console.log(`[translateFormFields] Translating form description: "${formData.description}"`);
      const translatedDescription = await translateMessageText(formData.description, 'es', 'en');
      console.log(`[translateFormFields] Translation result: "${translatedDescription}"`);
      // Only set if translation is different from original (to avoid saving English as Spanish)
      if (translatedDescription && translatedDescription !== formData.description && translatedDescription.trim().length > 0) {
        translated.description_es = translatedDescription;
        translationCount++;
        console.log(`[translateFormFields] ✓ Form description translated successfully`);
      } else {
        console.warn(`[translateFormFields] ✗ Skipping form description translation - same as original: "${formData.description}" -> "${translatedDescription}"`);
        skippedCount++;
      }
    } else if (formData.description && formData.description_es && formData.description_es !== formData.description) {
      console.log(`[translateFormFields] Form description already has valid Spanish translation: "${formData.description_es}"`);
    }
    
    // 2. Translate standard field labels
    // Translate if no Spanish label exists, OR if Spanish label is same as English (incorrect translation)
    if (formData.fields && formData.fields.length > 0) {
      console.log(`[translateFormFields] Processing ${formData.fields.length} standard fields`);
      translated.fields = await Promise.all(
        formData.fields.map(async (field, index) => {
          const needsTranslation = field.label && (!field.label_es || field.label_es === field.label);
          if (needsTranslation) {
            console.log(`[translateFormFields] Translating field ${index + 1}: "${field.label}"`);
            const translatedLabel = await translateMessageText(field.label, 'es', 'en');
            console.log(`[translateFormFields] Translation result for "${field.label}": "${translatedLabel}"`);
            // Only set if translation is different from original (to avoid saving English as Spanish)
            if (translatedLabel && translatedLabel !== field.label && translatedLabel.trim().length > 0) {
              translationCount++;
              console.log(`[translateFormFields] ✓ Field ${index + 1} translated successfully`);
              return {
                ...field,
                label_es: translatedLabel,
              };
            } else {
              console.warn(`[translateFormFields] ✗ Skipping field ${index + 1} translation - same as original: "${field.label}" -> "${translatedLabel}"`);
              skippedCount++;
            }
          } else if (field.label_es && field.label_es !== field.label) {
            console.log(`[translateFormFields] Field ${index + 1} already has valid Spanish translation: "${field.label_es}"`);
          }
          return field;
        })
      );
    }
    
    // 3. Translate table field labels
    // Translate if no Spanish label exists, OR if Spanish label is same as English (incorrect translation)
    if (formData.tableFields && formData.tableFields.length > 0) {
      console.log(`[translateFormFields] Processing ${formData.tableFields.length} table fields`);
      translated.tableFields = await Promise.all(
        formData.tableFields.map(async (field, index) => {
          const needsTranslation = field.label && (!field.label_es || field.label_es === field.label);
          if (needsTranslation) {
            console.log(`[translateFormFields] Translating table field ${index + 1}: "${field.label}"`);
            const translatedLabel = await translateMessageText(field.label, 'es', 'en');
            // Only set if translation is different from original (to avoid saving English as Spanish)
            if (translatedLabel && translatedLabel !== field.label && translatedLabel.trim().length > 0) {
              translationCount++;
              return {
                ...field,
                label_es: translatedLabel,
              };
            } else {
              console.warn(`[translateFormFields] Skipping table field ${index + 1} translation - same as original: "${field.label}"`);
              skippedCount++;
            }
          }
          return field;
        })
      );
    }
    
    // 4. Translate header field labels (if any)
    // Translate if no Spanish label exists, OR if Spanish label is same as English (incorrect translation)
    if (formData.headerFields && formData.headerFields.length > 0) {
      console.log(`[translateFormFields] Processing ${formData.headerFields.length} header fields`);
      translated.headerFields = await Promise.all(
        formData.headerFields.map(async (field, index) => {
          const needsTranslation = field.label && (!field.label_es || field.label_es === field.label);
          if (needsTranslation) {
            console.log(`[translateFormFields] Translating header field ${index + 1}: "${field.label}"`);
            const translatedLabel = await translateMessageText(field.label, 'es', 'en');
            // Only set if translation is different from original (to avoid saving English as Spanish)
            if (translatedLabel && translatedLabel !== field.label && translatedLabel.trim().length > 0) {
              translationCount++;
              return {
                ...field,
                label_es: translatedLabel,
              };
            } else {
              console.warn(`[translateFormFields] Skipping header field ${index + 1} translation - same as original: "${field.label}"`);
              skippedCount++;
            }
          }
          return field;
        })
      );
    }
    
    // 5. Translate table item names
    if (formData.itemsDefinition?.items && formData.itemsDefinition.items.length > 0) {
      const translatedItems = await Promise.all(
        formData.itemsDefinition.items.map(async (item) => {
          const translated = await translateMessageText(item, 'es', 'en');
          // Only return translation if it's different from original (to avoid saving English as Spanish)
          return (translated && translated !== item && translated.trim().length > 0) ? translated : item;
        })
      );
      // Only set items_es if at least one item was actually translated
      const hasRealTranslations = translatedItems.some((translated, index) => 
        translated !== formData.itemsDefinition!.items![index]
      );
      if (hasRealTranslations) {
        translated.itemsDefinition = {
          ...formData.itemsDefinition,
          items_es: translatedItems,
        };
      }
    }
    
    console.log(`[translateFormFields] Translation complete: ${translationCount} translated, ${skippedCount} skipped (same as original)`);
    return { success: true, data: translated };
  } catch (error: any) {
    console.error('[translateFormFields] Error translating form fields:', error);
    return { success: false, error: error.message || 'Translation failed' };
  }
}

